import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { localToday } from "@/components/FormBits";
import { SButton, sInput } from "@/components/staff/Bits";
import { LOGO } from "@/components/PublicLayout";

const directions = (v) => (v.location
  ? `https://www.google.com/maps/dir/?api=1&destination=${v.location.lat},${v.location.lng}`
  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.address || ""}, General Santos City`)}`);

const fmt12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

const groupByTeam = (visits) => visits.reduce((m, v) => {
  const key = v.visitors.map((x) => x.name).sort().join(" & ");
  return { ...m, [key]: [...(m[key] || []), v] };
}, {});

function TeamSheet({ team, visits, dayLabel, first }) {
  return (
    <section data-testid="print-team-section" className={`bg-white border border-[#E7DFD5] rounded-xl p-6 mb-6 print:border-0 print:rounded-none print:p-0 print:mb-0 ${first ? "" : "print:break-before-page"}`}>
      <header className="flex items-center gap-4 border-b-2 border-[#4A2E1A] pb-3 mb-4">
        <img src={LOGO} alt="" className="w-14 h-14 object-contain" />
        <div className="flex-1">
          <p className="font-serif-head font-black text-[#4A2E1A] text-lg leading-tight">MABUHAY CARE — Home Visit Sheet</p>
          <p className="text-sm">Saint Francis of Assisi Parish · {dayLabel}</p>
        </div>
        <div className="text-right text-sm"><p className="font-bold">Visiting team</p><p data-testid="print-team-name">{team}</p></div>
      </header>
      <div className="overflow-x-auto print:overflow-visible"><table className="w-full min-w-[560px] print:min-w-0 text-[13px] border-collapse">
        <thead><tr className="text-left border-b border-[#4A2E1A]"><th className="py-1 pr-2">Time</th><th className="py-1 pr-2">Person / household</th><th className="py-1 pr-2">Address & contact</th><th className="py-1 pr-2">Purpose</th><th className="py-1 w-[92px]">Directions</th></tr></thead>
        <tbody>
          {visits.map((v) => (
            <tr key={v.id} data-testid={`print-row-${v.id}`} className="border-b border-[#D6CABD] align-top" style={{ breakInside: "avoid" }}>
              <td className="py-2 pr-2 font-bold whitespace-nowrap">{fmt12(v.time)}</td>
              <td className="py-2 pr-2"><p className="font-bold">{v.person_name}</p><p className="font-mono text-[11px]">{v.case_reference}</p>
                <p className="text-[11px]">Household: {v.household_confirmation}</p>{v.vulnerable_involved && <p className="text-[11px] font-bold">Two-visitor rule — never alone with a child or vulnerable adult</p>}</td>
              <td className="py-2 pr-2">{v.address || "—"}{v.contact_number && <p className="mt-1">Tel: {v.contact_number}</p>}</td>
              <td className="py-2 pr-2">{v.purpose}</td>
              <td className="py-2"><QRCodeSVG value={directions(v)} size={80} level="M" /><p className="text-[10px] mt-0.5">Scan for map</p></td>
            </tr>))}
        </tbody>
      </table></div>
      <div className="mt-4 grid grid-cols-2 gap-6 text-[12px]">
        <p>Notes after visit (record in MABUHAY CARE, no health details):<br /><span className="block border-b border-[#B5A898] h-6" /><span className="block border-b border-[#B5A898] h-6" /></p>
        <p>Visitor signatures:<br /><span className="block border-b border-[#B5A898] h-6" /><span className="block border-b border-[#B5A898] h-6" /></p>
      </div>
      <p className="mt-4 text-[11px] text-[#605349]">CONFIDENTIAL — for the assigned visiting team only. Do not leave this sheet behind or share it. Return or shred after use.</p>
    </section>
  );
}

export default function VisitPrint() {
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || localToday();
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    api.get("/visits", { params: { date_from: day, date_to: day } })
      .then((r) => setVisits(r.data.filter((v) => v.status === "scheduled").sort((a, b) => a.time.localeCompare(b.time))))
      .catch((e) => toast.error(errMsg(e)));
  }, [day]);
  const dayLabel = new Date(`${day}T00:00`).toLocaleDateString("en-PH", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const teams = Object.entries(groupByTeam(visits || []));
  return (
    <div data-testid="visit-print-page">
      <div className="no-print flex flex-wrap items-end gap-3 mb-6">
        <Link to="/office/visits" className="inline-flex items-center gap-1 text-sm font-semibold mr-auto" data-testid="print-back-link"><ArrowLeft className="w-4 h-4" /> Home visits</Link>
        <input type="date" data-testid="print-date-input" className={`${sInput} w-auto`} value={day} onChange={(e) => setParams({ date: e.target.value })} />
        <SButton data-testid="print-button" disabled={!teams.length} onClick={() => window.print()}><Printer className="w-4 h-4" /> Print</SButton>
      </div>
      {!visits ? <p>Loading…</p> : teams.length === 0
        ? <p data-testid="print-empty" className="text-[15px] text-[#605349]">No scheduled visits on {dayLabel}.</p>
        : teams.map(([team, list], i) => <TeamSheet key={team} team={team} visits={list} dayLabel={dayLabel} first={i === 0} />)}
    </div>
  );
}
